import { useEffect } from 'react';

import { clsx } from 'clsx';
import {
  LastUserReviewResponse,
  UserReviewRequestType,
  UserReviewStatusType,
  useGetLastRequestCompliance,
} from '@ping/api';
import { t } from '@ping/helpers';
import {
  compliancyPendingRequestSeenStore,
  isCompliancyPendingRequestSeenSelector,
  useCompliancyPendingRequestSeenStore,
} from '@ping/stores/compliancy-pending-request-seen.store';
import {
  isThereAnyPendingRequestToNotifyUserSelector,
  lastRequestStatusSelector,
  useUserInformationStore,
} from '@ping/stores/userInformation.store';
import { pendingApprovalModalRemoteStateSelector, useModalStore } from '@ping/stores/modal.store';
import ClockIcon from '@ping/assets/Icon/clock.svg';
import { ButtonA11Y } from '@ping/uikit';

import style from './style.module.scss';

export const PendingApprovalTriggerButton = () => {
  const pendingApprovalModal = useModalStore(pendingApprovalModalRemoteStateSelector);
  const activeRequestStatus = useUserInformationStore(lastRequestStatusSelector);
  const isThereAnyPendingRequestToNotifyUser = useUserInformationStore(isThereAnyPendingRequestToNotifyUserSelector);
  const isCompliancyPendingRequestSeen = useCompliancyPendingRequestSeenStore(isCompliancyPendingRequestSeenSelector);
  const { setCompliancyPendingRequestSeen } = compliancyPendingRequestSeenStore;
  const { data: activeRequestData } = useGetLastRequestCompliance<LastUserReviewResponse>();

  useEffect(() => {
    setCompliancyPendingRequestSeen(
      activeRequestData ? Boolean(activeRequestData?.acknowledgedAt) : isCompliancyPendingRequestSeen
    );
  }, [activeRequestData?.statusType, activeRequestData?.acknowledgedAt]);

  if (
    activeRequestData?.statusType === UserReviewStatusType.Approved &&
    activeRequestData.requestType === UserReviewRequestType.Downgrade &&
    activeRequestData?.acknowledgedAt === undefined
  ) {
    return (
      <ButtonA11Y
        className={clsx(style['pending-approval--btn'], style['is-rejected'])}
        onPress={pendingApprovalModal.open}
      >
        <ClockIcon />
        <span className={style['pending-approval--btn-text']}>{t('Request was rejected')}</span>
      </ButtonA11Y>
    );
  }

  if (
    activeRequestData === ('' as LastUserReviewResponse) ||
    activeRequestData?.statusType === UserReviewStatusType.Approved
  ) {
    return null;
  }

  if (
    (activeRequestStatus === UserReviewStatusType.Denied && !isCompliancyPendingRequestSeen) ||
    isThereAnyPendingRequestToNotifyUser
  ) {
    return (
      <ButtonA11Y
        className={clsx(style['pending-approval--btn'], {
          [style['is-rejected']]: activeRequestStatus === UserReviewStatusType.Denied,
          [style['is-more-data-needed']]: activeRequestStatus === UserReviewStatusType.RequestedMoreData,
          [style['is-pending-for-review']]: activeRequestStatus === UserReviewStatusType.PendingForReview,
          [style['is-open']]: activeRequestStatus === UserReviewStatusType.Open,
        })}
        onPress={pendingApprovalModal.open}
      >
        <ClockIcon />
        <span className={style['pending-approval--btn-text']}>
          {activeRequestStatus === UserReviewStatusType.Denied && t('Request was rejected')}
          {activeRequestStatus === UserReviewStatusType.RequestedMoreData && t('Provide more data')}
          {activeRequestStatus === UserReviewStatusType.Open && t('Review in progress')}
          {activeRequestStatus !== UserReviewStatusType.Denied &&
            activeRequestStatus !== UserReviewStatusType.RequestedMoreData &&
            activeRequestStatus !== UserReviewStatusType.Open &&
            t('Pending for review')}
        </span>
      </ButtonA11Y>
    );
  }

  return null;
};
